<script lang="ts">
export default {
  name: 'AIVerificationResultsPage',
};
</script>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { DateTime } from 'luxon';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import Badge from '@/components/ui/Badge.vue';
import UISelect from '@/components/ui/UISelect.vue';
import {
  Clock,
  FileText,
  User,
  Loader,
  AlertCircle,
  History,
  BotIcon,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
} from 'lucide-vue-next';
import { CollapsibleRoot, CollapsibleContent, CollapsibleTrigger } from 'reka-ui';
import type { AIPromptResult, AIPromptResultWithReviews, ModelInvocation, ResultsPageItem, ModelInvocationStatus } from '@/types/ai';
import type { ParsedField } from '@/types/field';
import { aiService } from '@/services/AIService';
import { useFieldData } from '@/composables/useFieldData';
import { useModelInvocation } from '@/composables/useModelInvocation';
import PdfCanvas from '@/components/document/PdfCanvas.vue';
import ExtractedFieldsList from '@/components/document/ExtractedFieldsList.vue';

const route = useRoute();
const router = useRouter();

// State
const isLoading = ref(false);
const isLoadingDocument = ref(false);
const error = ref<string>('');
const promptResults = ref<AIPromptResultWithReviews[]>([]);
const activeInvocations = ref<ModelInvocation[]>([]);
const selectedResult = ref<AIPromptResultWithReviews | null>(null);
const pdfUrl = ref<string | null>(null);
const isResultsExpanded = ref(true);
const overriddenFields = ref<string[]>([]);

// Filter state
const selectedPromptFilter = ref<string>('');
const selectedUserFilter = ref<string>('');
const selectedStatusFilter = ref<string>('');

// Polling state
const { pollInvocationStatus, stopPollingInvocation, stopAllPolling } = useModelInvocation();

// Field data composable
const { fields, selectedField, fieldSelectionTrigger, error: fieldError, rawOutput, parseJsonString, selectField, clearFields } = useFieldData();

// Computed - unique prompts for filter dropdown
const uniquePrompts = computed(() => {
  const prompts = new Set<string>();
  allResults.value.forEach((item) => {
    if (isPromptResult(item) && item.promptName) {
      prompts.add(item.promptName);
    }
  });
  return [
    { value: '', label: 'All Prompts' },
    ...Array.from(prompts)
      .sort()
      .map((name) => ({ value: name, label: name })),
  ];
});

// Computed - unique users for filter dropdown
const uniqueUsers = computed(() => {
  const users = new Set<string>();
  allResults.value.forEach((item) => {
    if (item.userEmail) {
      users.add(item.userEmail);
    }
  });
  return [
    { value: '', label: 'All Users' },
    ...Array.from(users)
      .sort()
      .map((email) => ({ value: email, label: email })),
  ];
});

// Type guards
const isInvocation = (item: ResultsPageItem): item is ModelInvocation => {
  return 'invocationId' in item && 'status' in item;
};

const isPromptResult = (item: ResultsPageItem): item is AIPromptResultWithReviews => {
  return 'id' in item && 'rawOutput' in item;
};

// Computed - combined results and invocations
const allResults = computed(() => {
  const items: ResultsPageItem[] = [];

  // Add completed results
  if (promptResults.value && promptResults.value.length > 0) {
    items.push(...promptResults.value);
  }

  // Add active invocations (ensure it's an array)
  if (activeInvocations.value && activeInvocations.value.length > 0) {
    items.push(...activeInvocations.value);
  }

  // Sort by created date descending
  return items.sort((a, b) => {
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();
    return dateB - dateA;
  });
});

// Computed - filtered and sorted results
const sortedResults = computed(() => {
  let results = [...allResults.value];

  // Filter out malformed results - only show valid ones
  results = results.filter((r) => {
    if (isInvocation(r)) return true;
    if (isPromptResult(r)) return getResultValidation(r);
    return true;
  });

  // Apply prompt filter
  if (selectedPromptFilter.value) {
    results = results.filter((r) => {
      if (isInvocation(r)) {
        return false;
      }
      return (r.promptName || 'Unknown Prompt') === selectedPromptFilter.value;
    });
  }

  // Apply user filter
  if (selectedUserFilter.value) {
    results = results.filter((r) => {
      return r.userEmail === selectedUserFilter.value;
    });
  }

  // Apply status filter
  if (selectedStatusFilter.value) {
    results = results.filter((r) => {
      if (selectedStatusFilter.value === 'In Progress') {
        return isInvocation(r) && (r.status === 'PENDING' || r.status === 'RUNNING');
      } else if (selectedStatusFilter.value === 'Completed') {
        return isPromptResult(r);
      } else if (selectedStatusFilter.value === 'Failed') {
        return isInvocation(r) && r.status === 'FAILED';
      }
      return true; // 'All'
    });
  }

  return results;
});

// Computed - unique statuses for filter
const statusFilterOptions = computed(() => {
  return [
    { value: '', label: 'All' },
    { value: 'Completed', label: 'Completed' },
    { value: 'In Progress', label: 'In Progress' },
    { value: 'Failed', label: 'Failed' },
  ];
});

// Lifecycle
onMounted(async () => {
  await loadResults();
  await loadActiveInvocations();

  // Check for resultId query param and auto-select the result
  const resultIdParam = route.query.resultId as string;
  if (resultIdParam) {
    const id = parseInt(resultIdParam, 10);
    if (!isNaN(id)) {
      const match = promptResults.value.find((r) => r.id === id);
      if (match) {
        selectedResult.value = match;
      }
    }
  }
});

onUnmounted(() => {
  stopAllPolling();
});

// Watch for result selection to load document and collapse results
watch(selectedResult, async (newResult) => {
  if (newResult) {
    isResultsExpanded.value = false; // Collapse when result selected
    await loadDocumentAndFields(newResult);
  } else {
    // Clear document and fields
    pdfUrl.value = null;
    clearFields();
    isResultsExpanded.value = true; // Expand when no result selected
  }
});

// Sync selectedResult to URL query params
watch(selectedResult, (newResult) => {
  const query = { ...route.query };
  if (newResult) {
    const idStr = String(newResult.id);
    if (query.resultId !== idStr) {
      query.resultId = idStr;
      router.replace({ query });
    }
  } else if (query.resultId) {
    delete query.resultId;
    router.replace({ query });
  }
});

// Watch for route query changes to handle resultId from browser back/forward
watch(
  () => route.query.resultId,
  (newResultId) => {
    if (newResultId && typeof newResultId === 'string') {
      const id = parseInt(newResultId, 10);
      if (!isNaN(id) && selectedResult.value?.id !== id) {
        const match = promptResults.value.find((r) => r.id === id);
        if (match) {
          selectedResult.value = match;
        }
      }
    } else if (!newResultId && selectedResult.value) {
      selectedResult.value = null;
    }
  },
);

// Watch active invocations to auto-start/stop polling
watch(activeInvocations, (newInvocations, oldInvocations) => {
  // Start polling for new invocations that need it
  if (newInvocations && Array.isArray(newInvocations)) {
    newInvocations.forEach((inv) => {
      if (inv.status === 'PENDING' || inv.status === 'RUNNING') {
        // Check if this is a new invocation or status changed to PENDING/RUNNING
        const oldInv = oldInvocations?.find((old) => old.invocationId === inv.invocationId);
        if (!oldInv || (oldInv.status !== 'PENDING' && oldInv.status !== 'RUNNING')) {
          startPollingInvocation(inv.invocationId);
        }
      }
    });
  }
}, { deep: true });

// Functions
const loadResults = async () => {
  isLoading.value = true;
  error.value = '';

  try {
    promptResults.value = await aiService.getPromptResultsWithReviews();
  } catch (err: any) {
    error.value = err.message || 'Failed to load results';
    promptResults.value = [];
    console.error('Failed to load results:', err);
  } finally {
    isLoading.value = false;
  }
};

const loadActiveInvocations = async () => {
  try {
    const invocations = await aiService.getActiveInvocations(50);
    activeInvocations.value = invocations || [];
    // Start polling for each active invocation
    if (activeInvocations.value.length > 0) {
      activeInvocations.value.forEach((inv) => {
        if (inv.status === 'PENDING' || inv.status === 'RUNNING') {
          startPollingInvocation(inv.invocationId);
        }
      });
    }
  } catch (err: any) {
    console.error('Failed to load active invocations:', err);
    activeInvocations.value = [];
  }
};

const startPollingInvocation = (invocationId: string) => {
  pollInvocationStatus(
    invocationId,
    async (statusResponse) => {
      if (statusResponse.status === 'COMPLETED') {
        // Stop polling
        stopPollingInvocation(invocationId);

        // Remove from active invocations
        if (activeInvocations.value) {
          activeInvocations.value = activeInvocations.value.filter(
            (inv) => inv.invocationId !== invocationId
          );
        }

        // Fetch the result and add to promptResults
        try {
          const result = await aiService.getInvocationResult(invocationId);
          // Add default review values since this is a new result
          const resultWithReviews: AIPromptResultWithReviews = {
            ...result,
            reviews: [],
            reviewCount: 0,
          };
          promptResults.value.unshift(resultWithReviews);
        } catch (err) {
          console.error('Failed to get invocation result:', err);
        }
      } else if (statusResponse.status === 'FAILED') {
        // Stop polling
        stopPollingInvocation(invocationId);

        // Update the invocation in the list
        if (activeInvocations.value) {
          const invIndex = activeInvocations.value.findIndex(
            (inv) => inv.invocationId === invocationId
          );
          if (invIndex !== -1) {
            activeInvocations.value[invIndex].status = 'FAILED';
            if (statusResponse.errorMessage) {
              activeInvocations.value[invIndex].errorMessage = statusResponse.errorMessage;
            }
          }
        }
      }
    },
    (error) => {
      console.error('Polling error:', error);
      // Continue polling on error
    },
    (statusResponse) => {
      // Update status in active invocations
      if (activeInvocations.value) {
        const invIndex = activeInvocations.value.findIndex(
          (inv) => inv.invocationId === invocationId
        );
        if (invIndex !== -1) {
          activeInvocations.value[invIndex].status = statusResponse.status;
          if (statusResponse.errorMessage) {
            activeInvocations.value[invIndex].errorMessage = statusResponse.errorMessage;
          }
        }
      }
    }
  );
};

const loadDocumentAndFields = async (result: AIPromptResult) => {
  isLoadingDocument.value = true;

  try {
    // Fetch merged result to get overrides applied
    try {
      const mergedResult = await aiService.getMergedPromptResult(result.id);
      // Use mergedOutput (with overrides applied) for field parsing
      if (mergedResult.mergedOutput) {
        parseJsonString(mergedResult.mergedOutput);
      } else if (result.rawOutput) {
        parseJsonString(result.rawOutput);
      }
      // Track which fields have overrides for visual indicators
      overriddenFields.value = mergedResult.overriddenFields || [];
    } catch (mergeErr) {
      // Fallback to rawOutput if merged endpoint fails
      console.warn('Failed to fetch merged result, using rawOutput:', mergeErr);
      if (result.rawOutput) {
        parseJsonString(result.rawOutput);
      }
      overriddenFields.value = [];
    }

    // Load the PDF document - prefer platform document (documentUuid) over AI playground document (documentArn)
    if (result.documentUuid && result.documentAccountId) {
      // Platform document - use documentService with UUID and accountId
      const { documentService } = await import('@/services/v2/DocumentService');
      const blobUrl = await documentService.downloadDocumentFile(result.documentAccountId, result.documentUuid, true);
      pdfUrl.value = blobUrl;
    } else if (result.documentArn) {
      // AI playground document (backward compatibility)
      const blobUrl = await aiService.downloadDocument(result.documentArn, true);
      pdfUrl.value = blobUrl;
    } else {
      pdfUrl.value = null;
    }
  } catch (err: any) {
    console.error('Failed to load document:', err);
    pdfUrl.value = null;
  } finally {
    isLoadingDocument.value = false;
  }
};

const formatDate = (dateString: string) => {
  return DateTime.fromISO(dateString).toLocaleString(DateTime.DATETIME_MED);
};

const getModelDisplayName = (modelArn: string) => {
  const parts = modelArn.split('/');
  return parts[parts.length - 1] || modelArn;
};

const handleSelectResult = (item: ResultsPageItem) => {
  // Only allow selection of completed results
  if (isInvocation(item)) {
    if (item.status === 'COMPLETED' && item.promptResultId) {
      // Find and select the corresponding result
      const result = promptResults.value.find((r) => r.id === item.promptResultId);
      if (result) {
        selectedResult.value = result;
      }
    }
    return; // Don't select in-progress invocations
  }

  // Item must be a prompt result at this point
  if (!isPromptResult(item)) return;

  // If clicking the same result, deselect it
  if (selectedResult.value?.id === item.id) {
    selectedResult.value = null;
  } else {
    selectedResult.value = item;
  }
};

const isResultSelected = (item: ResultsPageItem) => {
  if (isInvocation(item)) {
    return item.status === 'COMPLETED' && item.promptResultId === selectedResult.value?.id;
  }
  return selectedResult.value?.id === item.id;
};

const getItemKey = (item: ResultsPageItem): string => {
  if (isInvocation(item)) {
    return `invocation-${item.invocationId}`;
  }
  return `result-${item.id}`;
};

const getStatusVariant = (status: ModelInvocationStatus): 'outline' | 'destructive' => {
  switch (status) {
    case 'PENDING':
      return 'outline';
    case 'RUNNING':
      return 'outline';
    case 'COMPLETED':
      return 'outline';
    case 'FAILED':
      return 'destructive';
    default:
      return 'outline';
  }
};

const getStatusBadgeClass = (status: ModelInvocationStatus): string => {
  switch (status) {
    case 'PENDING':
      return 'tw:border-yellow-200 tw:bg-yellow-50 tw:text-yellow-700';
    case 'RUNNING':
      return 'tw:border-blue-200 tw:bg-blue-50 tw:text-blue-700';
    case 'COMPLETED':
      return 'tw:border-green-200 tw:bg-green-50 tw:text-green-700';
    case 'FAILED':
      return 'tw:border-red-200 tw:bg-red-50 tw:text-red-700';
    default:
      return '';
  }
};

const handleFieldSelected = (field: ParsedField) => {
  selectField(field);
};

const handleReviewCompleted = async () => {
  // Refresh results to show updated review info
  await loadResults();
  // Also refresh the selected result's data if still selected
  if (selectedResult.value) {
    const updatedResult = promptResults.value.find((r) => r.id === selectedResult.value?.id);
    if (updatedResult) {
      selectedResult.value = updatedResult;
    }
  }
};

const formatReviewDate = (dateString: string) => {
  return DateTime.fromISO(dateString).toRelative();
};

/**
 * Extract JSON array from a string that may contain extra text before/after
 */
const extractJsonArray = (str: string): string | null => {
  const firstBracket = str.indexOf('[');
  const lastBracket = str.lastIndexOf(']');
  if (firstBracket === -1 || lastBracket === -1 || lastBracket < firstBracket) {
    return null;
  }
  return str.slice(firstBracket, lastBracket + 1);
};

/**
 * Check if a result's rawOutput can be parsed as valid extracted fields
 */
const isResultValid = (result: AIPromptResult): boolean => {
  if (!result.rawOutput) return false;

  try {
    let parsed: unknown;
    try {
      parsed = JSON.parse(result.rawOutput);
    } catch {
      const extracted = extractJsonArray(result.rawOutput);
      if (!extracted) return false;
      parsed = JSON.parse(extracted);
    }

    // Check if it's an array with at least one field that has name and type
    if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].name && parsed[0].type) {
      return true;
    }
    return false;
  } catch {
    return false;
  }
};

// Cache validation results to avoid re-parsing
const validationCache = ref<Map<number, boolean>>(new Map());

const getResultValidation = (result: AIPromptResult): boolean => {
  if (!validationCache.value.has(result.id)) {
    validationCache.value.set(result.id, isResultValid(result));
  }
  return validationCache.value.get(result.id) ?? false;
};
</script>

<template>
  <div class="tw:min-h-full tw:bg-linear-to-br tw:from-slate-50 tw:to-blue-50 tw:p-6">
    <div class="tw:mx-auto tw:max-w-7xl">
      <div class="tw:flex tw:h-full tw:flex-col tw:gap-4">
        <!-- Header -->
        <div class="tw:shrink-0 tw:space-y-2 tw:text-center">
          <h1 class="tw:bg-clip-text tw:text-3xl tw:font-bold">AI Data Extraction Results</h1>
          <p class="tw:mx-auto tw:max-w-2xl tw:text-sm tw:text-slate-600">
            View verified AI data extraction results with model, document, and user information
          </p>
        </div>

        <!-- Results List Card - Collapsible -->
        <div class="tw:shrink-0">
          <Card class="tw:relative tw:border-slate-200 tw:bg-white/80 tw:backdrop-blur-sm">
            <CollapsibleRoot v-model:open="isResultsExpanded">
              <CardHeader class="tw:pb-2">
                <div class="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-4">
                  <CollapsibleTrigger
                    as-child
                    :disabled="sortedResults.length === 0">
                    <div
                      :class="`tw:flex tw:items-center tw:gap-2 tw:rounded-md tw:p-2 tw:hover:bg-secondary/10 tw:transition-colors ${sortedResults.length > 0 ? 'tw:cursor-pointer' : ''}`">
                      <ChevronUp
                        v-if="sortedResults.length > 0 && isResultsExpanded"
                        class="tw:h-5 tw:w-5 tw:text-slate-400" />
                      <ChevronDown
                        v-else-if="sortedResults.length > 0"
                        class="tw:h-5 tw:w-5 tw:text-slate-400" />
                      <History class="tw:h-5 tw:w-5 tw:text-blue-600" />
                      <CardTitle class="tw:text-lg">Execution Results</CardTitle>
                      <Badge
                        v-if="sortedResults.length > 0"
                        variant="outline">
                        {{ sortedResults.length }}
                      </Badge>
                    </div>
                  </CollapsibleTrigger>

                  <!-- Filters -->
                  <div
                    v-if="allResults.length > 0"
                    class="tw:flex tw:items-center tw:gap-3"
                    @click.stop>
                    <div class="tw:flex tw:items-center tw:gap-2">
                      <label class="tw:text-sm tw:font-medium tw:text-slate-600">Status:</label>
                      <UISelect
                        v-model="selectedStatusFilter"
                        :options="statusFilterOptions"
                        placeholder="All"
                        class="tw:w-[140px]" />
                    </div>
                    <div class="tw:flex tw:items-center tw:gap-2">
                      <label class="tw:text-sm tw:font-medium tw:text-slate-600">Prompt:</label>
                      <UISelect
                        v-model="selectedPromptFilter"
                        :options="uniquePrompts"
                        placeholder="All Prompts"
                        class="tw:w-[180px]" />
                    </div>
                    <div class="tw:flex tw:items-center tw:gap-2">
                      <label class="tw:text-sm tw:font-medium tw:text-slate-600">User:</label>
                      <UISelect
                        v-model="selectedUserFilter"
                        :options="uniqueUsers"
                        placeholder="All Users"
                        class="tw:w-[200px]" />
                    </div>
                  </div>
                </div>
              </CardHeader>

              <CardContent>
                <!-- Loading State -->
                <div
                  v-if="isLoading"
                  class="tw:flex tw:items-center tw:justify-center tw:py-8">
                  <Loader class="tw:mr-2 tw:h-6 tw:w-6 tw:animate-spin tw:text-blue-600" />
                  <span class="tw:text-slate-600">Loading results...</span>
                </div>

                <!-- Error State -->
                <div
                  v-else-if="error"
                  class="tw:flex tw:items-center tw:justify-center tw:py-8">
                  <AlertCircle class="tw:mr-2 tw:h-6 tw:w-6 tw:text-red-500" />
                  <span class="tw:text-red-600">{{ error }}</span>
                </div>

                <!-- Empty State -->
                <div
                  v-else-if="sortedResults.length === 0"
                  class="tw:py-12 tw:text-center">
                  <Clock class="tw:mx-auto tw:mb-4 tw:h-12 tw:w-12 tw:text-slate-300" />
                  <h3 class="tw:mb-2 tw:text-lg tw:font-medium tw:text-slate-700">No Results Yet</h3>
                  <p class="tw:mx-auto tw:max-w-sm tw:text-sm tw:text-slate-500">
                    No verified results available yet.
                  </p>
                </div>

                <!-- Selected Result Preview (shown when collapsed) -->
                <div
                  v-else-if="!isResultsExpanded && selectedResult"
                  class="tw:rounded-lg tw:border tw:border-blue-500 tw:bg-blue-50 tw:p-3 tw:shadow-sm">
                  <div class="tw:flex tw:flex-1 tw:flex-wrap tw:items-center tw:gap-4">
                    <!-- Created At -->
                    <div class="tw:flex tw:items-center tw:gap-1 tw:text-sm tw:text-slate-500">
                      <Clock class="tw:h-4 tw:w-4" />
                      <span>{{ formatDate(selectedResult.createdAt) }}</span>
                    </div>

                    <!-- Model -->
                    <div class="tw:flex tw:min-w-[50] tw:items-center tw:gap-1">
                      <BotIcon class="tw:h-4 tw:w-4 tw:text-blue-500" />
                      <Badge
                        variant="outline"
                        class="tw:rounded-md tw:px-2 tw:py-1 tw:text-xs">
                        {{ getModelDisplayName(selectedResult.modelArn) }}
                      </Badge>
                    </div>

                    <!-- Document -->
                    <div class="tw:flex tw:min-w-[50px] tw:items-center tw:gap-1">
                      <FileText class="tw:h-4 tw:w-4 tw:text-green-500" />
                      <Badge
                        variant="outline"
                        class="tw:rounded-md tw:px-2 tw:py-1 tw:text-xs">
                        {{ selectedResult.documentName || 'No document' }}
                      </Badge>
                    </div>

                    <!-- User -->
                    <div class="tw:flex tw:min-w-[50px] tw:items-center tw:gap-1">
                      <User class="tw:h-4 tw:w-4 tw:text-purple-500" />
                      <Badge
                        variant="outline"
                        class="tw:rounded-md tw:px-2 tw:py-1 tw:text-xs">
                        {{ selectedResult.userEmail }}
                      </Badge>
                    </div>

                    <!-- Right-aligned section: Review Status + Validation Badge -->
                    <div class="tw:ml-auto tw:flex tw:items-center tw:gap-3">
                      <!-- Review Status -->
                      <div
                        v-if="selectedResult.reviewCount > 0"
                        class="tw:flex tw:items-center tw:gap-1">
                        <CheckCircle2 class="tw:h-4 tw:w-4 tw:text-green-600" />
                        <span class="tw:text-xs tw:text-green-700">
                          Reviewed by {{ selectedResult.reviews[0]?.userEmail }}
                        </span>
                        <span class="tw:text-xs tw:text-slate-400">
                          {{ formatReviewDate(selectedResult.reviews[0]?.reviewedAt) }}
                        </span>
                        <Badge
                          v-if="selectedResult.reviewCount > 1"
                          variant="outline"
                          class="tw:rounded-full tw:px-1.5 tw:py-0 tw:text-[10px]">
                          +{{ selectedResult.reviewCount - 1 }}
                        </Badge>
                      </div>

                      <!-- Validation Status Badge -->
                      <Badge
                        variant="outline"
                        class="tw:flex tw:items-center tw:gap-1 tw:rounded-md tw:border-green-200 tw:bg-green-50 tw:px-2 tw:py-1 tw:text-xs tw:text-green-700">
                        <CheckCircle2 class="tw:h-3 tw:w-3" />
                        Valid
                      </Badge>
                    </div>
                  </div>
                </div>

                <!-- Results List (shown when expanded) -->
                <CollapsibleContent>
                  <div
                    v-if="sortedResults.length > 0"
                    class="tw:max-h-[300px] tw:space-y-2 tw:overflow-y-auto">
                    <div
                      v-for="item in sortedResults"
                      :key="getItemKey(item)"
                      @click="handleSelectResult(item)"
                      :class="[
                        'tw:flex tw:items-center tw:justify-between tw:rounded-lg tw:border tw:p-3 tw:transition-all',
                        isResultSelected(item)
                          ? 'tw:border-blue-500 tw:bg-blue-50 tw:shadow-sm'
                          : isInvocation(item) && (item.status === 'PENDING' || item.status === 'RUNNING')
                          ? 'tw:border-slate-200 tw:bg-slate-50 tw:cursor-not-allowed tw:opacity-75'
                          : 'tw:border-slate-200 tw:bg-white tw:hover:border-slate-300 tw:hover:bg-slate-50',
                        isInvocation(item) && item.status === 'FAILED'
                          ? 'tw:border-red-200 tw:bg-red-50'
                          : '',
                      ]">
                      <div class="tw:flex tw:flex-1 tw:flex-wrap tw:items-center tw:gap-4">
                        <!-- Status Icon (for invocations) -->
                        <div v-if="isInvocation(item)" class="tw:flex tw:items-center tw:gap-1">
                          <Loader
                            v-if="item.status === 'RUNNING'"
                            class="tw:h-4 tw:w-4 tw:animate-spin tw:text-blue-600" />
                          <Clock
                            v-else-if="item.status === 'PENDING'"
                            class="tw:h-4 tw:w-4 tw:animate-pulse tw:text-yellow-600" />
                          <CheckCircle2
                            v-else-if="item.status === 'COMPLETED'"
                            class="tw:h-4 tw:w-4 tw:text-green-600" />
                          <AlertCircle
                            v-else-if="item.status === 'FAILED'"
                            class="tw:h-4 tw:w-4 tw:text-red-600" />
                        </div>

                        <!-- Created At -->
                        <div class="tw:flex tw:items-center tw:gap-1 tw:text-sm tw:text-slate-500">
                          <Clock v-if="!isInvocation(item) || item.status !== 'RUNNING'" class="tw:h-4 tw:w-4" />
                          <span>{{ formatDate(item.createdAt) }}</span>
                        </div>

                        <!-- Status Badge (for invocations) -->
                        <div v-if="isInvocation(item)" class="tw:flex tw:items-center tw:gap-1">
                          <Badge
                            :variant="getStatusVariant(item.status)"
                            :class="`tw:rounded-md tw:px-2 tw:py-1 tw:text-xs ${getStatusBadgeClass(item.status)}`">
                            {{ item.status }}
                          </Badge>
                        </div>

                        <!-- Model -->
                        <div class="tw:flex tw:min-w-[50px] tw:items-center tw:gap-1">
                          <BotIcon class="tw:h-4 tw:w-4 tw:text-blue-500" />
                          <Badge
                            variant="outline"
                            class="tw:rounded-md tw:px-2 tw:py-1 tw:text-xs">
                            {{ getModelDisplayName(item.modelArn) }}
                          </Badge>
                        </div>

                        <!-- Document -->
                        <div class="tw:flex tw:min-w-[50px] tw:items-center tw:gap-1">
                          <FileText class="tw:h-4 tw:w-4 tw:text-green-500" />
                          <Badge
                            variant="outline"
                            class="tw:rounded-md tw:px-2 tw:py-1 tw:text-xs">
                            {{ item.documentName || 'No document' }}
                          </Badge>
                        </div>

                        <!-- User -->
                        <div class="tw:flex tw:min-w-[50px] tw:items-center tw:gap-1">
                          <User class="tw:h-4 tw:w-4 tw:text-purple-500" />
                          <Badge
                            variant="outline"
                            class="tw:rounded-md tw:px-2 tw:py-1 tw:text-xs">
                            {{ item.userEmail }}
                          </Badge>
                        </div>

                        <!-- Error Message (for failed invocations) -->
                        <div v-if="isInvocation(item) && item.status === 'FAILED' && item.errorMessage" class="tw:flex tw:items-center tw:gap-1 tw:text-xs tw:text-red-600">
                          <AlertCircle class="tw:h-3 tw:w-3" />
                          <span class="tw:max-w-xs tw:truncate">{{ item.errorMessage }}</span>
                        </div>

                        <!-- Right-aligned section: Review Status + Validation Badge -->
                        <div v-if="isPromptResult(item)" class="tw:ml-auto tw:flex tw:items-center tw:gap-3">
                          <!-- Review Status -->
                          <div
                            v-if="item.reviewCount > 0"
                            class="tw:flex tw:items-center tw:gap-1">
                            <CheckCircle2 class="tw:h-4 tw:w-4 tw:text-green-600" />
                            <span class="tw:text-xs tw:text-green-700">
                              Reviewed by {{ item.reviews[0]?.userEmail }}
                            </span>
                            <span class="tw:text-xs tw:text-slate-400">
                              {{ formatReviewDate(item.reviews[0]?.reviewedAt) }}
                            </span>
                            <Badge
                              v-if="item.reviewCount > 1"
                              variant="outline"
                              class="tw:rounded-full tw:px-1.5 tw:py-0 tw:text-[10px]">
                              +{{ item.reviewCount - 1 }}
                            </Badge>
                          </div>

                          <!-- Validation Status Badge (always Valid since malformed are filtered out) -->
                          <Badge
                            variant="outline"
                            class="tw:flex tw:items-center tw:gap-1 tw:rounded-md tw:border-green-200 tw:bg-green-50 tw:px-2 tw:py-1 tw:text-xs tw:text-green-700">
                            <CheckCircle2 class="tw:h-3 tw:w-3" />
                            Valid
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                </CollapsibleContent>
              </CardContent>
            </CollapsibleRoot>
          </Card>
        </div>

        <!-- Document Viewer and Extracted Fields - 2/3 | 1/3 layout -->
        <div
          v-if="selectedResult"
          class="tw:grid tw:h-[calc(100vh-380px)] tw:max-h-[900px] tw:min-h-[400px] tw:grid-cols-3 tw:gap-4 tw:overflow-hidden">
          <!-- Document Viewer - 2/3 width -->
          <Card class="tw:relative tw:col-span-2 tw:flex tw:min-h-0 tw:flex-col tw:overflow-hidden tw:border-slate-200 tw:bg-white/80 tw:backdrop-blur-sm">
            <!-- Loading Overlay -->
            <div
              v-if="isLoadingDocument"
              class="tw:absolute tw:inset-0 tw:z-20 tw:flex tw:items-center tw:justify-center tw:rounded-lg tw:bg-white/90">
              <div class="tw:flex tw:flex-col tw:items-center tw:gap-2">
                <Loader class="tw:h-8 tw:w-8 tw:animate-spin tw:text-blue-600" />
                <span class="tw:text-slate-600">Loading document...</span>
              </div>
            </div>

            <CardHeader class="tw:shrink-0 tw:pb-2">
              <CardTitle class="tw:flex tw:items-center tw:gap-2 tw:text-lg">
                <FileText class="tw:h-5 tw:w-5 tw:text-green-600" />
                Document Viewer
              </CardTitle>
            </CardHeader>
            <CardContent class="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:overflow-hidden tw:p-0">
              <PdfCanvas
                :pdf-url="pdfUrl"
                :selected-field="selectedField"
                :selection-trigger="fieldSelectionTrigger" />
            </CardContent>
          </Card>

          <!-- Extracted Fields - 1/3 width -->
          <Card class="tw:col-span-1 tw:flex tw:min-h-0 tw:flex-col tw:overflow-hidden tw:border-slate-200 tw:bg-white/80 tw:backdrop-blur-sm">
            <ExtractedFieldsList
              :fields="fields"
              :selected-field="selectedField"
              :error="fieldError"
              :raw-output="rawOutput"
              :prompt-result-id="selectedResult?.id"
              :overridden-fields="overriddenFields"
              @field-selected="handleFieldSelected"
              @overrides-changed="loadDocumentAndFields(selectedResult!)"
              @review-completed="handleReviewCompleted" />
          </Card>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
/* Collapsible animation */
.CollapsibleContent {
  overflow: hidden;
}

.CollapsibleContent[data-state='open'] {
  animation: slideDown 200ms ease-out;
}

.CollapsibleContent[data-state='closed'] {
  animation: slideUp 200ms ease-out;
}

@keyframes slideDown {
  from {
    height: 0;
    opacity: 0;
  }
  to {
    height: var(--reka-collapsible-content-height);
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    height: var(--reka-collapsible-content-height);
    opacity: 1;
  }
  to {
    height: 0;
    opacity: 0;
  }
}
</style>
